import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function ServiceRequestPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Get selected equipment types
    const equipmentTypes: string[] = [];
    ['mri', 'ct', 'ultrasound', 'xray', 'mammography'].forEach(type => {
      if (formData.get(type) === 'on') {
        equipmentTypes.push(type.toUpperCase());
      }
    });
    formData.set('equipment_types', equipmentTypes.join(', '));

    // Get selected service types
    const serviceTypes: string[] = [];
    ['rental', 'turnkey', 'staffing', 'maintenance'].forEach(type => {
      if (formData.get(`service_${type}`) === 'on') {
        serviceTypes.push(type.charAt(0).toUpperCase() + type.slice(1));
      }
    });
    formData.set('service_types', serviceTypes.join(', '));

    try {
      const response = await fetch('https://readdy.ai/api/form/d63t30dsi3r5mpi9v49g', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section 
          ref={heroRef}
          className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #0F1C2E 0%, #1A2942 50%, #243B53 100%)'
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-slate-blue rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-teal-300 text-sm font-medium mb-6">
                Schedule a Consultation
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Request Mobile Imaging <span className="text-teal-400">Equipment Services</span>
              </h1>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed">
                Complete the form below to schedule a consultation with our team. We'll assess your facility's needs and provide a customized mobile imaging solution.
              </p>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section ref={formRef} className="py-12 sm:py-20 px-4 sm:px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className={`grid lg:grid-cols-3 gap-8 lg:gap-12 transition-all duration-700 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              
              {/* Left Column - Info */}
              <div className="lg:col-span-1 order-2 lg:order-1">
                <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 sticky top-28">
                  <h3 className="text-xl font-bold text-navy-dark mb-6">What to Expect</h3>
                  
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-teal-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy-dark text-sm mb-1">Initial Call</h4>
                        <p className="text-gray-600 text-sm">We'll contact you within 24 hours to discuss your needs</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-teal-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy-dark text-sm mb-1">Site Assessment</h4>
                        <p className="text-gray-600 text-sm">Our team evaluates your facility for optimal equipment placement</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-3-line text-teal-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy-dark text-sm mb-1">Custom Proposal</h4>
                        <p className="text-gray-600 text-sm">Receive a tailored solution with transparent pricing</p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-truck-line text-teal-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy-dark text-sm mb-1">Rapid Deployment</h4>
                        <p className="text-gray-600 text-sm">Equipment delivered and operational within 48 hours</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <p className="text-gray-600 text-sm mb-4">Need immediate assistance?</p>
                    <a 
                      href="mailto:admin@medilinemedical.com" 
                      className="flex items-center gap-3 text-navy-dark font-semibold hover:text-teal-600 transition-colors cursor-pointer"
                    >
                      <div className="w-10 h-10 bg-navy-dark rounded-full flex items-center justify-center">
                        <i className="ri-mail-fill text-white"></i>
                      </div>
                      admin@medilinemedical.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="lg:col-span-2 order-1 lg:order-2">
                <form 
                  id="equipment-consultation-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm border border-gray-100"
                >
                  <h2 className="text-2xl font-bold text-navy-dark mb-2">Equipment Consultation Request</h2>
                  <p className="text-gray-600 mb-8">Fill out the form below and our team will get back to you shortly.</p>

                  {/* Facility Information */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-navy-dark mb-4 flex items-center gap-2">
                      <i className="ri-building-2-line text-teal-600"></i>
                      Facility Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Facility Name *</label>
                        <input
                          type="text"
                          name="facility_name"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                          placeholder="Enter facility name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Facility Type *</label>
                        <select
                          name="facility_type"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        >
                          <option value="">Select facility type</option>
                          <option value="Hospital">Hospital</option>
                          <option value="Outpatient Clinic">Outpatient Clinic</option>
                          <option value="Urgent Care">Urgent Care</option>
                          <option value="Imaging Center">Imaging Center</option>
                          <option value="Surgery Center">Surgery Center</option>
                          <option value="Skilled Nursing Facility">Skilled Nursing Facility</option>
                          <option value="Rehabilitation Center">Rehabilitation Center</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Facility Address *</label>
                        <input
                          type="text"
                          name="facility_address"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                          placeholder="Street address, City, State, ZIP"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-navy-dark mb-4 flex items-center gap-2">
                      <i className="ri-user-line text-teal-600"></i>
                      Contact Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Name *</label>
                        <input
                          type="text"
                          name="contact_name"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Job Title *</label>
                        <input
                          type="text"
                          name="job_title"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                          placeholder="Your position"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                          placeholder="email@facility.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                          placeholder="(555) 555-5555"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Equipment Needed */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-navy-dark mb-4 flex items-center gap-2">
                      <i className="ri-heart-pulse-line text-teal-600"></i>
                      Equipment Needed
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">Select all equipment types you're interested in:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {[
                        { id: 'mri', label: 'MRI', icon: 'ri-scan-line' },
                        { id: 'ct', label: 'CT Scanner', icon: 'ri-donut-chart-line' },
                        { id: 'ultrasound', label: 'Ultrasound', icon: 'ri-pulse-line' },
                        { id: 'xray', label: 'X-Ray', icon: 'ri-contrast-2-line' },
                        { id: 'mammography', label: 'Mammography', icon: 'ri-heart-3-line' }
                      ].map((item) => (
                        <label 
                          key={item.id}
                          className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-all has-[:checked]:border-teal-500 has-[:checked]:bg-teal-50"
                        >
                          <input type="checkbox" name={item.id} className="sr-only" />
                          <div className="w-10 h-10 flex items-center justify-center">
                            <i className={`${item.icon} text-2xl text-gray-600`}></i>
                          </div>
                          <span className="text-sm font-medium text-gray-700 text-center">{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Service Type */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-navy-dark mb-4 flex items-center gap-2">
                      <i className="ri-service-line text-teal-600"></i>
                      Service Type
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">What services are you interested in?</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { id: 'service_rental', label: 'Equipment Rental', desc: 'Short or long-term mobile unit rental' },
                        { id: 'service_turnkey', label: 'Turnkey Solutions', desc: 'Complete imaging department setup' },
                        { id: 'service_staffing', label: 'Staffing Services', desc: 'Certified technologists provided' },
                        { id: 'service_maintenance', label: 'Maintenance & Support', desc: 'Ongoing technical support' }
                      ].map((item) => (
                        <label 
                          key={item.id}
                          className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-all has-[:checked]:border-teal-500 has-[:checked]:bg-teal-50"
                        >
                          <input type="checkbox" name={item.id} className="mt-1" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">{item.label}</span>
                            <span className="text-xs text-gray-500">{item.desc}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Timeline & Details */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-navy-dark mb-4 flex items-center gap-2">
                      <i className="ri-calendar-line text-teal-600"></i>
                      Timeline & Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">When do you need equipment?</label>
                        <select
                          name="timeline"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        >
                          <option value="">Select timeline</option>
                          <option value="Immediately (within 48 hours)">Immediately (within 48 hours)</option>
                          <option value="Within 1 week">Within 1 week</option>
                          <option value="Within 2 weeks">Within 2 weeks</option>
                          <option value="Within 1 month">Within 1 month</option>
                          <option value="Planning for future">Planning for future</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Expected Duration</label>
                        <select
                          name="duration"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        >
                          <option value="">Select duration</option>
                          <option value="1-7 days">1-7 days</option>
                          <option value="1-4 weeks">1-4 weeks</option>
                          <option value="1-3 months">1-3 months</option>
                          <option value="3-6 months">3-6 months</option>
                          <option value="6+ months">6+ months</option>
                          <option value="Permanent solution">Permanent solution</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Estimated Patient Volume</label>
                        <select
                          name="patient_volume"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        >
                          <option value="">Select volume</option>
                          <option value="Less than 10 per day">Less than 10 per day</option>
                          <option value="10-25 per day">10-25 per day</option>
                          <option value="25-50 per day">25-50 per day</option>
                          <option value="50+ per day">50+ per day</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Contact Method</label>
                        <select
                          name="contact_method"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                        >
                          <option value="Phone">Phone</option>
                          <option value="Email">Email</option>
                          <option value="Either">Either</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-navy-dark mb-4 flex items-center gap-2">
                      <i className="ri-file-text-line text-teal-600"></i>
                      Additional Information
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Please describe your specific needs or any questions you have
                      </label>
                      <textarea
                        name="additional_info"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                        placeholder="Tell us about your imaging needs, current challenges, or any specific requirements..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                    </div>
                  </div>

                  {/* How did you hear about us */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">How did you hear about Mediline?</label>
                    <select
                      name="referral_source"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Google Search">Google Search</option>
                      <option value="Industry Referral">Industry Referral</option>
                      <option value="Conference/Trade Show">Conference/Trade Show</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Previous Customer">Previous Customer</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto px-8 py-4 bg-navy-dark text-white font-semibold rounded-lg hover:bg-navy-dark/90 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          Submit Consultation Request
                        </>
                      )}
                    </button>
                    <p className="text-gray-500 text-sm text-center sm:text-left">
                      We'll respond within 24 business hours
                    </p>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <p className="text-green-800 font-medium">Request Submitted Successfully!</p>
                        <p className="text-green-700 text-sm">Our team will contact you within 24 hours to discuss your needs.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-error-warning-line text-red-600 text-xl"></i>
                      </div>
                      <div>
                        <p className="text-red-800 font-medium">Submission Failed</p>
                        <p className="text-red-700 text-sm">Please try again or call us directly at (800) 555-1234.</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-12 sm:py-16 px-4 sm:px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                { icon: 'ri-shield-check-line', label: 'HIPAA Compliant', desc: 'Full regulatory compliance' },
                { icon: 'ri-time-line', label: '24/7 Support', desc: 'Round-the-clock assistance' },
                { icon: 'ri-map-pin-line', label: 'California-Wide', desc: 'Statewide coverage' },
                { icon: 'ri-award-line', label: 'Certified Techs', desc: 'Licensed professionals' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-teal-600 text-2xl`}></i>
                  </div>
                  <h4 className="font-semibold text-navy-dark text-sm mb-1">{item.label}</h4>
                  <p className="text-gray-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
